import { HttpClient } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { CampaignTemplate } from "src/app/backend/models/campaign-template";
import { MessageTemplateType } from "src/app/backend/models/message-template";
import {
  ReminderTemplate,
  ReminderTemplateCreate
} from "src/app/backend/models/reminder-template";
import { UserNotify } from "src/app/support/notify-decorator";
import { omitUndefinedAndNullProperties } from "src/app/support/object";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";

@Injectable()
export class ReminderTemplateBackend {
  private readonly http = inject(HttpClient);

  load(id: string): Observable<ReminderTemplate> {
    return this.http.get<ReminderTemplate>(
      `${environment.snoots.api.notification}/templates/${id}`
    );
  }

  list(request: PageRequest): Observable<PaginatedResult<ReminderTemplate>> {
    return this.http.get<PaginatedResult<ReminderTemplate>>(
      `${environment.snoots.api.notification}/templates`,
      {
        params: omitUndefinedAndNullProperties({
          p: request.page,
          s: request.pageSize
        })
      }
    );
  }

  listByType({
    page,
    pageSize,
    type
  }: {
    page: number;
    pageSize: number;
    type: MessageTemplateType;
  }): Observable<PaginatedResult<ReminderTemplate | CampaignTemplate>> {
    return this.http.get<PaginatedResult<ReminderTemplate | CampaignTemplate>>(
      `${environment.snoots.api.notification}/templates`,
      {
        params: { p: page, s: pageSize, type }
      }
    );
  }

  listAllByType(type: MessageTemplateType): Observable<ReminderTemplate[]> {
    return this.listByType({ page: 1, pageSize: 10000, type }).pipe(
      map((m) => m.docs)
    );
  }

  listAll(): Observable<ReminderTemplate[]> {
    return this.list({ page: 1, pageSize: 10000 }).pipe(map((m) => m.docs));
  }

  listAllByTypes(types: string[]): Observable<ReminderTemplate[]> {
    return this.listAll().pipe(
      map((x) => {
        return x.filter((doc) => types.includes(doc.content));
      })
    );
  }

  @UserNotify({
    successMessage: marker("Modello creato"),
    errorMessage: marker("Impossibile creare il modello"),
    progressIndeterminate: true,
    progressMessage: marker("Creazione modello")
  })
  create(payload: ReminderTemplateCreate): Observable<ReminderTemplate> {
    return this.http.post<ReminderTemplate>(
      `${environment.snoots.api.notification}/templates`,
      payload
    );
  }

  update(id: string, request: Partial<ReminderTemplate>): Observable<void> {
    return this.http.patch<void>(
      `${environment.snoots.api.notification}/templates/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Modello eliminato"),
    errorMessage: marker("Impossibile eliminare il modello")
  })
  delete(id: string): Observable<void> {
    return this.http.delete<void>(
      `${environment.snoots.api.notification}/templates/${id}`
    );
  }

  @UserNotify({
    successMessage: marker("Modello clonato"),
    errorMessage: marker("Impossibile clonare il modello")
  })
  clone(id: string): Observable<void> {
    return this.http.post<void>(
      `${environment.snoots.api.notification}/templates/${id}/clones`,
      {}
    );
  }
}
